export const ROLE_ENUM_VALUES = Object.freeze(['ROLE_USER', 'ROLE_GUEST', 'ROLE_ADMIN'])
export const AI_PROVIDER_VALUES = Object.freeze(['CHAT_GPT'])
export const STREAM_TYPE_VALUES = Object.freeze(['TELEGRAM', 'X', 'YANDEX_DZEN', 'WORDPRESS', 'UNKNOWN'])
export const SCHEMA_PARAM_VALUES = Object.freeze(['JSON_SCHEMA', 'SCHEMA'])
export const CONTENT_STATUS_VALUES = Object.freeze(['CREATED', 'DRAFT', 'READY', 'PUBLISHED', 'ERROR', 'ARCHIVE'])
export const RSS_CATEGORY_HINTS = Object.freeze(['NEWS', 'OTHER', 'Новости', 'Другое'])
export const ADMIN_STATISTICS_SORT_BY_VALUES = Object.freeze([
  'userId',
  'username',
  'streamsTotal',
  'contentsTotal',
  'publishedContentsCount',
  'costUsd',
])
export const SORT_DIRECTION_VALUES = Object.freeze(['asc', 'desc'])
export const AI_MODEL_KIND_VALUES = Object.freeze([
  'llm',
  'embedding',
  'reranker',
  'search',
  'image',
  'speech',
  'moderation',
  'other',
])
export const AI_PRICING_STATUS_VALUES = Object.freeze(['DRAFT', 'ACTIVE', 'ARCHIVED'])

const hasEnumValue = (values, value) => values.includes(String(value || ''))
const toNumber = value => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}
const toNullableNumber = value => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
const toStringValue = value => String(value == null ? '' : value).trim()
const toNullableString = value => {
  const normalized = toStringValue(value)
  return normalized || null
}
const toBoolean = value => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true
    if (['false', '0', 'no', 'off'].includes(normalized)) return false
  }
  return Boolean(value)
}
const toObjectOrNull = value => {
  if (value === null || value === undefined) return null
  if (typeof value === 'object' && !Array.isArray(value)) return value
  return null
}
const toJsonNode = value => (value === undefined ? null : value)
const toIsoStringValue = value => {
  const raw = toStringValue(value)
  if (!raw) return ''
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toISOString()
}
const toDateOnlyString = value => {
  const raw = toStringValue(value)
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  return date.toISOString().slice(0, 10)
}

const normalizeRoles = input => {
  if (!Array.isArray(input)) return []
  const uniqueRoles = new Set()
  input.forEach(item => {
    if (hasEnumValue(ROLE_ENUM_VALUES, item)) uniqueRoles.add(item)
  })
  return [...uniqueRoles]
}

export const isRoleEnum = value => hasEnumValue(ROLE_ENUM_VALUES, value)
export const isAiProvider = value => hasEnumValue(AI_PROVIDER_VALUES, value)
export const isStreamType = value => hasEnumValue(STREAM_TYPE_VALUES, value)
export const isSchemaParam = value => hasEnumValue(SCHEMA_PARAM_VALUES, value)
export const isContentStatus = value => hasEnumValue(CONTENT_STATUS_VALUES, value)
export const isStatisticsSortBy = value => hasEnumValue(ADMIN_STATISTICS_SORT_BY_VALUES, value)
export const isSortDirection = value => hasEnumValue(SORT_DIRECTION_VALUES, String(value || '').toLowerCase())

export function normalizeAdminUserDto(payload = {}) {
  const rolesInput = Array.isArray(payload.roles) ? payload.roles : []
  return {
    id: toNumber(payload.id),
    username: toStringValue(payload.username),
    enabled: toBoolean(payload.enabled),
    accountNonExpired: toBoolean(payload.accountNonExpired),
    credentialsNonExpired: toBoolean(payload.credentialsNonExpired),
    accountNonLocked: toBoolean(payload.accountNonLocked),
    roles: normalizeRoles(rolesInput),
  }
}

export function normalizeAdminAiRequestProfileDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    profileKey: toStringValue(payload.profileKey),
    description: toNullableString(payload.description),
    isActive: toBoolean(payload.isActive),
    schemaDependsOnStreamType: toBoolean(payload.schemaDependsOnStreamType),
    requireProviderModelFromContext: toBoolean(payload.requireProviderModelFromContext),
    allowDefaultPrompt: toBoolean(payload.allowDefaultPrompt),
    defaultProvider: isAiProvider(payload.defaultProvider) ? payload.defaultProvider : null,
    defaultModel: toNullableString(payload.defaultModel),
    config: toObjectOrNull(payload.config),
    defaultPrompt: toNullableString(payload.defaultPrompt),
    version: toNumber(payload.version || 1),
    createdAt: toIsoStringValue(payload.createdAt),
    updatedAt: toIsoStringValue(payload.updatedAt),
  }
}

export function normalizeAdminSchemaEntryDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    profileKey: toStringValue(payload.profileKey),
    streamType: isStreamType(payload.streamType) ? payload.streamType : null,
    param: isSchemaParam(payload.param) ? payload.param : SCHEMA_PARAM_VALUES[0],
    value: toStringValue(payload.value),
  }
}

export function normalizePropertiesDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    label: toStringValue(payload.label),
    label2: toNullableString(payload.label2),
    param: toStringValue(payload.param),
    value: toStringValue(payload.value),
  }
}

export function normalizeAdminInviteDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    code: toStringValue(payload.code),
    note: toNullableString(payload.note),
    active: toBoolean(payload.active),
    used: toBoolean(payload.used),
    createdBy: toNullableString(payload.createdBy),
    createdAt: toNullableString(payload.createdAt),
    usedByEmail: toNullableString(payload.usedByEmail),
    usedByUserId: toNullableNumber(payload.usedByUserId),
    usedAt: toNullableString(payload.usedAt),
  }
}

export function normalizeStreamSettingDefinitionDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    streamType: isStreamType(payload.streamType) ? payload.streamType : STREAM_TYPE_VALUES[STREAM_TYPE_VALUES.length - 1],
    aiName: toStringValue(payload.aiName),
    category: toStringValue(payload.category),
    settingKey: toStringValue(payload.settingKey),
    settingType: toStringValue(payload.settingType),
    description: toNullableString(payload.description),
    defaultValue: toNullableString(payload.defaultValue),
    metadata: toObjectOrNull(payload.metadata),
    dependencies: toObjectOrNull(payload.dependencies),
  }
}

export function normalizeAdminRssCleanerDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    textSelector: toStringValue(payload.textSelector),
    deleteTags: toNullableString(payload.deleteTags),
    deleteWords: toNullableString(payload.deleteWords),
    attachedChannelId: toNullableNumber(payload.attachedChannelId),
    attachedChannelName: toNullableString(payload.attachedChannelName),
  }
}

export function normalizeAdminRssChannelDto(payload = {}) {
  const cleanerPayload = toObjectOrNull(payload.cleaner)
  return {
    id: toNumber(payload.id),
    name: toStringValue(payload.name),
    url: toStringValue(payload.url),
    description: toNullableString(payload.description),
    category: toStringValue(payload.category),
    disabled: toBoolean(payload.disabled),
    createdAt: toIsoStringValue(payload.createdAt),
    cleaner: cleanerPayload ? normalizeAdminRssCleanerDto(cleanerPayload) : null,
  }
}

export function normalizeAdminStreamDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    name: toNullableString(payload.name),
    description: toNullableString(payload.description),
    type: toNullableString(payload.type),
    settings: toObjectOrNull(payload.settings),
    createdAt: toIsoStringValue(payload.createdAt),
    updatedAt: toIsoStringValue(payload.updatedAt),
    ownerUserId: toNullableNumber(payload.ownerUserId),
    ownerUsername: toNullableString(payload.ownerUsername),
    credentialId: toNullableNumber(payload.credentialId),
    credentialLabel: toNullableString(payload.credentialLabel),
    contentsCount: toNumber(payload.contentsCount),
    publishedContentsCount: toNumber(payload.publishedContentsCount),
    aiRequestsCount: toNumber(payload.aiRequestsCount),
    imageSearchRequestsCount: toNumber(payload.imageSearchRequestsCount),
  }
}

export function normalizeAdminContentDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    externalId: toStringValue(payload.externalId),
    title: toStringValue(payload.title),
    body: toNullableString(payload.body),
    metaDescription: toStringValue(payload.metaDescription),
    leadParagraph: toStringValue(payload.leadParagraph),
    slug: toStringValue(payload.slug),
    image: toStringValue(payload.image),
    imageSearchQuery: toStringValue(payload.imageSearchQuery),
    imageAlt: toStringValue(payload.imageAlt),
    focusKw: toStringValue(payload.focusKw),
    sourceUrl: toStringValue(payload.sourceUrl),
    wpCategories: toJsonNode(payload.wpCategories),
    wpTags: toJsonNode(payload.wpTags),
    wpFeaturedMediaId: toNullableNumber(payload.wpFeaturedMediaId),
    wpTaxonomyIds: toJsonNode(payload.wpTaxonomyIds),
    status: isContentStatus(payload.status) ? payload.status : toStringValue(payload.status),
    createdAt: toIsoStringValue(payload.createdAt),
    updatedAt: toIsoStringValue(payload.updatedAt),
    publishedAt: toIsoStringValue(payload.publishedAt),
    streamId: toNumber(payload.streamId),
  }
}

export function normalizeContentFormField(payload = {}) {
  const options = Array.isArray(payload.options)
    ? payload.options.map(item => toStringValue(item)).filter(Boolean)
    : null

  return {
    name: toStringValue(payload.name),
    label: toStringValue(payload.label),
    type: toStringValue(payload.type),
    required: toBoolean(payload.required),
    minLength: payload.minLength == null ? null : toNumber(payload.minLength),
    maxLength: payload.maxLength == null ? null : toNumber(payload.maxLength),
    pattern: toNullableString(payload.pattern),
    options,
  }
}

export function normalizeContentFormSection(payload = {}) {
  const fields = Array.isArray(payload.fields) ? payload.fields : []
  return {
    id: toStringValue(payload.id),
    title: toStringValue(payload.title),
    order: toNumber(payload.order),
    fields: fields.map(normalizeContentFormField).filter(field => field.name),
  }
}

export function normalizeContentFormResponse(payload = {}) {
  const sections = Array.isArray(payload.sections) ? payload.sections : []
  return {
    type: toStringValue(payload.type),
    version: toNumber(payload.version || 1),
    sections: sections.map(normalizeContentFormSection).sort((a, b) => a.order - b.order),
  }
}

export function normalizeAdminSystemStatisticsDto(payload = {}) {
  return {
    usersTotal: toNumber(payload.usersTotal),
    usersEnabled: toNumber(payload.usersEnabled),
    streamsTotal: toNumber(payload.streamsTotal),
    contentsTotal: toNumber(payload.contentsTotal),
    publishedContentsCount: toNumber(payload.publishedContentsCount),
    aiRequestsCount: toNumber(payload.aiRequestsCount),
    costUsd: toNumber(payload.costUsd),
    directLlmUsd: toNumber(payload.directLlmUsd),
    imageSearchUsd: toNumber(payload.imageSearchUsd),
    cmakerUsd: toNumber(payload.cmakerUsd),
  }
}

export function normalizeAdminUserStatisticsItemDto(payload = {}) {
  return {
    userId: toNumber(payload.userId),
    username: toStringValue(payload.username),
    enabled: toBoolean(payload.enabled),
    streamsTotal: toNumber(payload.streamsTotal),
    contentsTotal: toNumber(payload.contentsTotal),
    publishedContentsCount: toNumber(payload.publishedContentsCount),
    aiRequestsCount: toNumber(payload.aiRequestsCount),
    costUsd: toNumber(payload.costUsd),
    directLlmUsd: toNumber(payload.directLlmUsd),
    imageSearchUsd: toNumber(payload.imageSearchUsd),
    cmakerUsd: toNumber(payload.cmakerUsd),
  }
}

export function normalizeAdminStatisticsDto(payload = {}) {
  const users = Array.isArray(payload.users) ? payload.users : []
  return {
    userIdFilter: toNullableNumber(payload.userIdFilter),
    fromDate: toDateOnlyString(payload.fromDate),
    toDate: toDateOnlyString(payload.toDate),
    system: normalizeAdminSystemStatisticsDto(payload.system),
    users: users.map(normalizeAdminUserStatisticsItemDto),
  }
}

export function validateAdminUserRolesUpdateRequest(payload = {}) {
  const errors = []
  const roles = Array.isArray(payload.roles) ? payload.roles : []
  const normalizedRoles = roles.filter(role => isRoleEnum(role))
  if (!normalizedRoles.length) errors.push('Выберите минимум одну роль.')
  if (normalizedRoles.length !== roles.length) errors.push('Обнаружено недопустимое значение роли.')
  return errors
}

export function validateAdminUserPasswordResetRequest(payload = {}) {
  const errors = []
  const password = toStringValue(payload.newPassword)
  if (!password) errors.push('Введите новый пароль.')
  return errors
}

export function validateAdminAiProfileUpsertRequest(payload = {}) {
  const errors = []
  const requiresProviderModel = !toBoolean(payload.requireProviderModelFromContext)
  if (!toStringValue(payload.profileKey)) errors.push('Поле «Ключ профиля» (profileKey) обязательно.')

  if (requiresProviderModel && !isAiProvider(payload.defaultProvider)) {
    errors.push('Поле «Провайдер по умолчанию» (defaultProvider) обязательно, если провайдер/модель из контекста отключены.')
  }
  if (requiresProviderModel && !toStringValue(payload.defaultModel)) {
    errors.push('Поле «Модель по умолчанию» (defaultModel) обязательно, если провайдер/модель из контекста отключены.')
  }
  if (!requiresProviderModel && payload.defaultProvider && !isAiProvider(payload.defaultProvider)) {
    errors.push('Значение defaultProvider должно быть CHAT_GPT.')
  }
  return errors
}

export function validateAdminSchemaUpsertRequest(payload = {}) {
  const errors = []
  const rawValue = toStringValue(payload.value)

  if (!toStringValue(payload.profileKey)) errors.push('Поле «Ключ профиля» (profileKey) обязательно.')
  if (!isSchemaParam(payload.param)) errors.push('Поле param должно быть JSON_SCHEMA или SCHEMA.')
  if (!rawValue) errors.push('Поле value обязательно.')
  if (payload.streamType != null && payload.streamType !== '' && !isStreamType(payload.streamType)) {
    errors.push('Поле streamType содержит недопустимое значение.')
  }
  if (rawValue) {
    try {
      JSON.parse(rawValue)
    } catch {
      errors.push('Поле value должно быть валидной JSON-строкой.')
    }
  }
  return errors
}

export function validatePropertiesUpsertRequest(payload = {}) {
  const errors = []
  if (!toStringValue(payload.label)) errors.push('Поле label обязательно.')
  if (!toStringValue(payload.param)) errors.push('Поле param обязательно.')
  if (!toStringValue(payload.value)) errors.push('Поле value обязательно.')
  return errors
}

export function validateStreamSettingUpsertRequest(payload = {}) {
  const errors = []
  if (!isStreamType(payload.streamType)) errors.push('Поле streamType обязательно.')
  if (!toStringValue(payload.aiName)) errors.push('Поле aiName обязательно.')
  if (!toStringValue(payload.category)) errors.push('Поле category обязательно.')
  if (!toStringValue(payload.settingKey)) errors.push('Поле settingKey обязательно.')
  if (!toStringValue(payload.settingType)) errors.push('Поле settingType обязательно.')
  return errors
}

export function validateAdminRssChannelUpsertRequest(payload = {}, { partial = false } = {}) {
  const errors = []
  const hasName = Object.prototype.hasOwnProperty.call(payload, 'name')
  const hasUrl = Object.prototype.hasOwnProperty.call(payload, 'url')
  const hasCategory = Object.prototype.hasOwnProperty.call(payload, 'category')
  const hasCleanerId = Object.prototype.hasOwnProperty.call(payload, 'cleanerId')
  const hasClearCleaner = Object.prototype.hasOwnProperty.call(payload, 'clearCleaner')

  if ((!partial || hasName) && !toStringValue(payload.name)) errors.push('Поле name обязательно.')
  if ((!partial || hasUrl) && !toStringValue(payload.url)) errors.push('Поле url обязательно.')
  if ((!partial || hasCategory) && !toStringValue(payload.category)) errors.push('Поле category обязательно.')

  const cleanerId = toNullableNumber(payload.cleanerId)
  if (hasCleanerId && payload.cleanerId !== null && payload.cleanerId !== '' && cleanerId == null) {
    errors.push('Поле cleanerId должно быть числом.')
  }

  if (hasClearCleaner && toBoolean(payload.clearCleaner) && hasCleanerId && cleanerId != null) {
    errors.push('Нельзя одновременно передавать cleanerId и clearCleaner=true.')
  }

  return errors
}

export function validateAdminRssCleanerUpsertRequest(payload = {}, { partial = false } = {}) {
  const errors = []
  const hasTextSelector = Object.prototype.hasOwnProperty.call(payload, 'textSelector')
  if ((!partial || hasTextSelector) && !toStringValue(payload.textSelector)) {
    errors.push('Поле textSelector обязательно.')
  }
  return errors
}

export function validateAdminStreamOwnerUpdateRequest(payload = {}) {
  const errors = []
  const userId = toNumber(payload.userId)
  if (!userId) errors.push('Поле userId обязательно и должно быть числом больше 0.')
  return errors
}

export function buildAdminUserStatusUpdateRequest(payload = {}) {
  return {
    enabled: payload.enabled == null ? null : toBoolean(payload.enabled),
    accountNonExpired: payload.accountNonExpired == null ? null : toBoolean(payload.accountNonExpired),
    credentialsNonExpired: payload.credentialsNonExpired == null ? null : toBoolean(payload.credentialsNonExpired),
    accountNonLocked: payload.accountNonLocked == null ? null : toBoolean(payload.accountNonLocked),
  }
}

export function buildAdminUserRolesUpdateRequest(payload = {}) {
  const roles = Array.isArray(payload.roles) ? payload.roles : []
  return { roles: normalizeRoles(roles) }
}

export function buildAdminUserPasswordResetRequest(payload = {}) {
  return { newPassword: toStringValue(payload.newPassword) }
}

export function buildAdminAiProfileUpsertRequest(payload = {}) {
  return {
    profileKey: toStringValue(payload.profileKey),
    description: toNullableString(payload.description),
    isActive: toBoolean(payload.isActive),
    schemaDependsOnStreamType: toBoolean(payload.schemaDependsOnStreamType),
    requireProviderModelFromContext: toBoolean(payload.requireProviderModelFromContext),
    allowDefaultPrompt: toBoolean(payload.allowDefaultPrompt),
    defaultProvider: isAiProvider(payload.defaultProvider) ? payload.defaultProvider : null,
    defaultModel: toNullableString(payload.defaultModel),
    config: payload.config == null ? null : toObjectOrNull(payload.config),
    defaultPrompt: toNullableString(payload.defaultPrompt),
    version: toNumber(payload.version || 1),
  }
}

export function buildAdminSchemaUpsertRequest(payload = {}) {
  return {
    profileKey: toStringValue(payload.profileKey),
    streamType: payload.streamType ? payload.streamType : null,
    param: payload.param,
    value: toStringValue(payload.value),
  }
}

export function buildPropertiesUpsertRequest(payload = {}) {
  return {
    label: toStringValue(payload.label),
    label2: toNullableString(payload.label2),
    param: toStringValue(payload.param),
    value: toStringValue(payload.value),
  }
}

export function buildCreateInviteRequest(payload = {}) {
  const count = Math.min(50, Math.max(1, Math.trunc(toNumber(payload.count) || 1)))
  return {
    count,
    note: toNullableString(payload.note),
  }
}

export function buildStreamSettingUpsertRequest(payload = {}) {
  return {
    streamType: payload.streamType,
    aiName: toStringValue(payload.aiName),
    category: toStringValue(payload.category),
    settingKey: toStringValue(payload.settingKey),
    settingType: toStringValue(payload.settingType),
    description: toNullableString(payload.description),
    defaultValue: toNullableString(payload.defaultValue),
    metadata: payload.metadata == null ? null : toObjectOrNull(payload.metadata),
    dependencies: payload.dependencies == null ? null : toObjectOrNull(payload.dependencies),
  }
}

export function buildAdminRssChannelUpsertRequest(payload = {}, { partial = false } = {}) {
  const request = {}
  const hasName = Object.prototype.hasOwnProperty.call(payload, 'name')
  const hasUrl = Object.prototype.hasOwnProperty.call(payload, 'url')
  const hasDescription = Object.prototype.hasOwnProperty.call(payload, 'description')
  const hasCategory = Object.prototype.hasOwnProperty.call(payload, 'category')
  const hasDisabled = Object.prototype.hasOwnProperty.call(payload, 'disabled')
  const hasCleanerId = Object.prototype.hasOwnProperty.call(payload, 'cleanerId')
  const hasClearCleaner = Object.prototype.hasOwnProperty.call(payload, 'clearCleaner')

  if (!partial || hasName) {
    request.name = partial ? toNullableString(payload.name) : toStringValue(payload.name)
  }
  if (!partial || hasUrl) {
    request.url = partial ? toNullableString(payload.url) : toStringValue(payload.url)
  }
  if (!partial || hasDescription) {
    request.description = toNullableString(payload.description)
  }
  if (!partial || hasCategory) {
    request.category = partial ? toNullableString(payload.category) : toStringValue(payload.category)
  }
  if (!partial || hasDisabled) {
    request.disabled = toBoolean(payload.disabled)
  }
  if (hasCleanerId) {
    const cleanerId = toNullableNumber(payload.cleanerId)
    if (cleanerId != null) request.cleanerId = cleanerId
  }
  if (hasClearCleaner) {
    request.clearCleaner = toBoolean(payload.clearCleaner)
  }

  return request
}

export function buildAdminRssCleanerUpsertRequest(payload = {}, { partial = false } = {}) {
  const request = {}
  const hasTextSelector = Object.prototype.hasOwnProperty.call(payload, 'textSelector')
  const hasDeleteTags = Object.prototype.hasOwnProperty.call(payload, 'deleteTags')
  const hasDeleteWords = Object.prototype.hasOwnProperty.call(payload, 'deleteWords')

  if (!partial || hasTextSelector) {
    request.textSelector = partial ? toNullableString(payload.textSelector) : toStringValue(payload.textSelector)
  }
  if (!partial || hasDeleteTags) {
    request.deleteTags = toNullableString(payload.deleteTags)
  }
  if (!partial || hasDeleteWords) {
    request.deleteWords = toNullableString(payload.deleteWords)
  }

  return request
}

export function buildAdminStreamUpdateRequest(payload = {}) {
  const request = {}

  if (Object.prototype.hasOwnProperty.call(payload, 'name')) request.name = toNullableString(payload.name)
  if (Object.prototype.hasOwnProperty.call(payload, 'description')) request.description = toNullableString(payload.description)
  if (toStringValue(payload.type)) request.type = toStringValue(payload.type)

  if (Object.prototype.hasOwnProperty.call(payload, 'settings')) {
    const normalizedSettings = toObjectOrNull(payload.settings)
    if (normalizedSettings) request.settings = normalizedSettings
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'credentialId')) {
    const credentialId = toNullableNumber(payload.credentialId)
    if (credentialId != null) request.credentialId = credentialId
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'clearCredential')) {
    request.clearCredential = toBoolean(payload.clearCredential)
  }

  return request
}

export function buildAdminStreamOwnerUpdateRequest(payload = {}) {
  return {
    userId: toNumber(payload.userId),
  }
}

// ---------- AI pricing ----------

const toInstantOrNull = value => {
  const iso = toIsoStringValue(value)
  return iso || null
}
const isAiPricingStatus = value => hasEnumValue(AI_PRICING_STATUS_VALUES, value)
export const isAiModelKind = value => hasEnumValue(AI_MODEL_KIND_VALUES, value)

export function normalizeAiModelPriceDto(payload = {}) {
  return {
    id: toNumber(payload.id),
    provider: toStringValue(payload.provider),
    model: toStringValue(payload.model),
    modelKind: toStringValue(payload.modelKind),
    inputPer1mUsd: toNullableNumber(payload.inputPer1mUsd),
    outputPer1mUsd: toNullableNumber(payload.outputPer1mUsd),
    cacheReadPer1mUsd: toNullableNumber(payload.cacheReadPer1mUsd),
    cacheWritePer1mUsd: toNullableNumber(payload.cacheWritePer1mUsd),
    reasoningPer1mUsd: toNullableNumber(payload.reasoningPer1mUsd),
    perRequestUsd: toNullableNumber(payload.perRequestUsd),
    effectiveFrom: toIsoStringValue(payload.effectiveFrom),
    enabled: payload.enabled == null ? true : toBoolean(payload.enabled),
    metadata: toObjectOrNull(payload.metadata),
  }
}

export function normalizeAiPricingSnapshotDto(payload = {}) {
  const models = Array.isArray(payload.models) ? payload.models : []
  return {
    id: toNumber(payload.id),
    status: isAiPricingStatus(payload.status) ? payload.status : toStringValue(payload.status),
    currency: toStringValue(payload.currency) || 'USD',
    sourceAsOf: toIsoStringValue(payload.sourceAsOf),
    comment: toNullableString(payload.comment),
    metadata: toObjectOrNull(payload.metadata),
    createdBy: toNullableNumber(payload.createdBy),
    updatedBy: toNullableNumber(payload.updatedBy),
    createdAt: toIsoStringValue(payload.createdAt),
    updatedAt: toIsoStringValue(payload.updatedAt),
    publishedAt: toIsoStringValue(payload.publishedAt),
    archivedAt: toIsoStringValue(payload.archivedAt),
    models: models.map(normalizeAiModelPriceDto),
  }
}

export function buildAiPricingSnapshotRequest(payload = {}) {
  return {
    currency: toStringValue(payload.currency) || 'USD',
    sourceAsOf: toInstantOrNull(payload.sourceAsOf),
    comment: toNullableString(payload.comment),
    metadata: payload.metadata == null ? null : toObjectOrNull(payload.metadata),
    copyFromActive: payload.copyFromActive == null ? true : toBoolean(payload.copyFromActive),
  }
}

export function buildAiModelPriceCreateRequest(payload = {}) {
  return {
    provider: toStringValue(payload.provider),
    model: toStringValue(payload.model),
    modelKind: toStringValue(payload.modelKind),
    inputPer1mUsd: toNullableNumber(payload.inputPer1mUsd),
    outputPer1mUsd: toNullableNumber(payload.outputPer1mUsd),
    cacheReadPer1mUsd: toNullableNumber(payload.cacheReadPer1mUsd),
    cacheWritePer1mUsd: toNullableNumber(payload.cacheWritePer1mUsd),
    reasoningPer1mUsd: toNullableNumber(payload.reasoningPer1mUsd),
    perRequestUsd: toNullableNumber(payload.perRequestUsd),
    effectiveFrom: toInstantOrNull(payload.effectiveFrom),
    enabled: payload.enabled == null ? true : toBoolean(payload.enabled),
    metadata: payload.metadata == null ? null : toObjectOrNull(payload.metadata),
  }
}

export function buildAiModelPriceUpdateRequest(payload = {}) {
  return {
    inputPer1mUsd: toNullableNumber(payload.inputPer1mUsd),
    outputPer1mUsd: toNullableNumber(payload.outputPer1mUsd),
    cacheReadPer1mUsd: toNullableNumber(payload.cacheReadPer1mUsd),
    cacheWritePer1mUsd: toNullableNumber(payload.cacheWritePer1mUsd),
    reasoningPer1mUsd: toNullableNumber(payload.reasoningPer1mUsd),
    perRequestUsd: toNullableNumber(payload.perRequestUsd),
    effectiveFrom: toInstantOrNull(payload.effectiveFrom),
    enabled: payload.enabled == null ? true : toBoolean(payload.enabled),
    metadata: payload.metadata == null ? null : toObjectOrNull(payload.metadata),
  }
}

export function validateAiPricingSnapshotRequest(payload = {}) {
  const errors = []
  const currency = toStringValue(payload.currency)
  if (!currency) errors.push('Поле «Валюта» (currency) обязательно.')
  if (currency && currency.length > 3) errors.push('Код валюты не длиннее 3 символов.')
  if (!toInstantOrNull(payload.sourceAsOf)) errors.push('Поле «Актуально на» (sourceAsOf) обязательно.')
  return errors
}

export function validateAiModelPriceCreateRequest(payload = {}) {
  const errors = []
  if (!toStringValue(payload.provider)) errors.push('Поле «Провайдер» (provider) обязательно.')
  if (!toStringValue(payload.model)) errors.push('Поле «Модель» (model) обязательно.')
  if (!isAiModelKind(payload.modelKind)) {
    errors.push(`Поле «Тип» (modelKind) должно быть одним из: ${AI_MODEL_KIND_VALUES.join(', ')}.`)
  }
  return errors
}
